import React from 'react';
import cx from 'classnames';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  TabsProps,
  TabProps,
} from '@reach/tabs';
import { TabListProps } from 'reakit/ts';

export const CKTabPanel = TabPanel;
export const CKTabPanels = TabPanels;

interface CKTabsProps extends TabsProps {
  isBordered?: boolean;
  isSegmented?: boolean;
  isSmall?: boolean;
}

export interface CKTabProps extends Partial<TabProps> {
  badge?: React.ReactNode;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  label: string;
  isSelected?: boolean | undefined;
  onClick?: () => void;
}

export interface CKTabListProps extends Partial<TabListProps> {
  className?: string;
}

interface TabsContextValue {
  isBordered?: boolean;
  isSegmented?: boolean;
  isSmall?: boolean;
}

const TabsContext = React.createContext<TabsContextValue>({
  isBordered: false,
  isSegmented: false,
  isSmall: false,
});

export const CKTabs: React.FC<CKTabsProps> = props => {
  const { isBordered, isSegmented, isSmall, ...rest } = props;

  return (
    <TabsContext.Provider
      value={{
        isBordered,
        isSegmented,
        isSmall,
      }}
    >
      <Tabs {...rest}></Tabs>
    </TabsContext.Provider>
  );
};

export const CKTab: React.FC<CKTabProps> = props => {
  const { badge, icon, iconOnly, label, ...rest } = props;
  const { isSelected } = rest;
  const { isSegmented } = React.useContext(TabsContext);

  const tabClass = cx({
    'ck-tab--active': isSelected,
    'ck-tab-segment': isSegmented,
    'ck-tab': !isSegmented,
  });
  return (
    <Tab {...rest} className={tabClass}>
      <div className="flex items-center" onClick={props.onClick}>
        {icon && (
          <span role="img" aria-label={label} className="ck-tab-icon">
            {icon}
          </span>
        )}
        {!iconOnly && <span>{rest.children}</span>}
        {badge && <span className="ck-badge ck-badge--rounded">{badge}</span>}
      </div>
    </Tab>
  );
};

export const CKTabList: React.FC<CKTabListProps> = props => {
  const { children, className, ...rest } = props;
  const tabListProps = rest as TabListProps;
  const { isSegmented, isBordered, isSmall, ...tab } = React.useContext(
    TabsContext
  );

  const tabListClass = cx('ck-tabs', className, {
    'ck-tabs--small': isSmall,
    'ck-tabs--bordered': isBordered,
  });

  return (
    <TabList {...tab} {...tabListProps} className={tabListClass}>
      {children}
    </TabList>
  );
};
